import * as React from "react";
import { registerAdminWorker } from "@shared/lib/pwa";

import { useTenant } from "@app/providers/tenant";
import {
  getAdminPushConfiguration,
  markAdminNotificationRead,
  registerAdminPushSubscription,
  revokeAdminPushSubscription,
} from "../api/notifications";
import { useTranslation } from "react-i18next";

export function useAdminPushNotifications() {
  const { t: tUi } = useTranslation("ui");
  const { tenantId, setTenantId } = useTenant();
  const [permission, setPermission] =
    React.useState<NotificationPermission>("default");
  const [subscription, setSubscription] =
    React.useState<PushSubscription | null>(null);
  const [publicKey, setPublicKey] = React.useState<string | null>(null);
  const [isSupported, setIsSupported] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const supported =
      typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      "PushManager" in window;
    setIsSupported(supported);
    if (!supported || !tenantId) return;

    let cancelled = false;
    setPermission(Notification.permission);
    void (async () => {
      try {
        const [configuration, registration] = await Promise.all([
          getAdminPushConfiguration(),
          registerAdminWorker(),
        ]);
        const current = await registration.pushManager.getSubscription();
        if (cancelled) return;
        setPublicKey(configuration.enabled ? configuration.publicKey : null);
        setSubscription(current);
        if (
          current &&
          configuration.enabled &&
          Notification.permission === "granted"
        ) {
          await registerAdminPushSubscription(current);
        }
      } catch {
        if (!cancelled) setPublicKey(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  React.useEffect(() => {
    if (typeof window === "undefined" || !tenantId) return;
    const currentUrl = new URL(window.location.href);
    const targetTenant = currentUrl.searchParams.get("tenant");
    const notificationId = currentUrl.searchParams.get("notification");
    if (!targetTenant && !notificationId) return;

    if (targetTenant && isGuid(targetTenant) && targetTenant !== tenantId)
      setTenantId(targetTenant);
    if (notificationId && isGuid(notificationId)) {
      void markAdminNotificationRead(notificationId).catch(() => undefined);
    }
    currentUrl.searchParams.delete("tenant");
    currentUrl.searchParams.delete("notification");
    window.history.replaceState(
      window.history.state,
      "",
      `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`,
    );
  }, [setTenantId, tenantId]);

  React.useEffect(() => {
    if (!isSupported) return;
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "storizone:admin-push-received") {
        window.dispatchEvent(new CustomEvent("storizone:admin-push-received"));
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [isSupported]);

  const subscribe = React.useCallback(async () => {
    if (!publicKey || !isSupported || isBusy) return null;
    setIsBusy(true);
    setError(null);
    try {
      const registration = await registerAdminWorker();
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return null;

      const existing = await registration.pushManager.getSubscription();
      const next =
        existing ||
        (await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }));
      await registerAdminPushSubscription(next);
      setSubscription(next);
      return next;
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : tUi("unableToEnableBrowserNotifications", {
              defaultValue: "Unable to enable browser notifications.",
            }),
      );
      return null;
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, isSupported, publicKey, tUi]);

  const unsubscribe = React.useCallback(async () => {
    if (!subscription || isBusy) return false;
    setIsBusy(true);
    setError(null);
    try {
      await revokeAdminPushSubscription(subscription.endpoint);
      await subscription.unsubscribe();
      setSubscription(null);
      return true;
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : tUi("unableToDisableBrowserNotifications", {
              defaultValue: "Unable to disable browser notifications.",
            }),
      );
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [isBusy, subscription, tUi]);

  return {
    permission,
    subscription,
    isConfigured: Boolean(publicKey),
    isSupported,
    isBusy,
    error,
    subscribe,
    unsubscribe,
  };
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from(rawData, (character) => character.codePointAt(0) ?? 0);
}

function isGuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}
